/**
 * Per-asset GARBAGE COLLECTION — Bombproof storage foundation, Layer 2
 * (board 12-imgstore deferred successor; imgstore-gc-s1).
 *
 * The content-addressed {@link AssetStore} holds image blobs BY REFERENCE and
 * DEDUPS across projects (one blob per distinct image, regardless of how many
 * documents reference it). Deleting a project reclaims its documents
 * (`projectStorageSweep`) but leaves its blobs — a storage LEAK. This module
 * identifies genuinely-orphaned blobs (and, while REPORT-ONLY below, only reports them).
 *
 * WHY A GLOBAL MARK-SWEEP, NEVER PER-PROJECT REF-DELETION. Because blobs are
 * shared by content, a blob is orphaned iff NO live document in ANY project
 * references its id. So GC marks every `imageStoreId` referenced by the whole
 * surviving document corpus, then sweeps the store of everything unmarked (while
 * REPORT-ONLY, "sweep" means LIST — see below).
 *
 * TRIGGER: BOOT-ONLY (PM-ratified imgstore-gc-s1 approval-gate §8). Runs once at
 * startup, after the document orphan-sweep, from `bootstrapStorageFoundation`.
 * There is deliberately NO per-delete trigger: a per-delete sweep in one tab
 * cannot see another tab's in-flight (in-memory) asset claims.
 *
 * ⚠️ REPORT-ONLY since hotfix-sweep-s2 (D1 containment, CTO 65-121/65-122): the
 * sweep DELETES NOTHING. imgstore-gc-s1 held that boot-only avoids the cross-tab
 * window. FALSIFIED (code-watch CW-1): a BOOT in tab B is the same window — tab A
 * has `put` a blob (claimed in A's memory only) whose document is not yet saved;
 * B's sweep saw it unreferenced and unclaimed and deleted it. Until storage-s1's
 * (d) supplies a cross-tab-safe liveness rule, the sweep computes and logs what it
 * WOULD delete. Accepted cost: unreferenced blobs are kept (see TECHNICAL-DEBT.md).
 *
 * TWO DESTRUCTIVE-SAFETY GUARANTEES (deleting a REFERENCED blob = a missing image
 * on next load = data loss, so both are load-bearing):
 *  1. **Refuse on ambiguity.** The referenced set must be COMPLETE. If the corpus
 *     cannot be fully enumerated + read (a document namespace enumerates to zero
 *     keys for a live project — the fail-silent `getKeysByPrefix() → []` case; or
 *     any document fails to load — an unparseable STRING is not a failure: it is
 *     scanned liberally, snapshots-p2a-s10 D8), {@link collectBootReferencedAssetIds}
 *     returns `complete: false` and {@link runBootAssetGc} refuses (lists nothing).
 *  2. **Session claim guard.** {@link AssetStore.claimedIds} — every blob this
 *     session `put` — is never listed (the in-flight-save race guard). It covers
 *     THIS tab only; see REPORT-ONLY above.
 *
 * MARK IS LIBERAL (over-retain is the safe asymmetry). It collects an
 * `imageStoreId` from ANY node that carries one, not only `type:"image"` nodes —
 * so a future non-image node that references a blob (a page background, a fill
 * pattern, a watermark) can never be missed and its blob wrongly swept. Retaining
 * a blob nothing references is a bounded leak; under-reading a reference is data
 * loss.
 */
import { AssetStore, ASSET_ID_PATTERN } from "./assetStore";
import { assetStore as productionAssetStore } from "./assetStoreSingleton";
import { indexedDBManager } from "../system/indexed-db";
import { isProjectRecordKey } from "../projectStorageSweep";
import { isRecoveryEnvelope } from "../recoveryJournal";
import { logger } from "../../utils/logger";

/**
 * The single field name a document uses to reference a stored asset. The asset
 * seam (`assetSeam.ts`) writes/reads this exact field; MARK reads the same one.
 * A future new reference field MUST be added here so MARK stays complete.
 */
const ASSET_REF_FIELD = "imageStoreId";

/**
 * Every substring (overlapping windows included) matching {@link ASSET_ID_PATTERN}.
 * Built from the owning format's `source`; a fresh global regex per call (no shared
 * `lastIndex` state).
 */
function markAssetIdSubstrings(text: string, out: Set<string>): void {
  const scan = new RegExp(`(?=(${ASSET_ID_PATTERN.source}))`, "g");
  for (const m of text.matchAll(scan)) out.add(m[1]);
}

/**
 * A STRING stored record (snapshots-p2a-s10 D8, B-6): parse it and walk the result; if it
 * does not parse to an object, scan its raw text LIBERALLY for every asset-id-shaped
 * substring. It may only over-mark, and it never makes the corpus incomplete.
 */
function markStoredString(text: string, out: Set<string>): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    markAssetIdSubstrings(text, out);
    return;
  }
  if (parsed && typeof parsed === "object") collectReferencedAssetIds(parsed, out);
  else markAssetIdSubstrings(text, out);
}

/**
 * LIBERAL MARK collector. Walk an arbitrarily-nested record and collect every
 * non-empty string value stored under `imageStoreId`, on ANY node (not only
 * `type:"image"`). Iterative DFS with a cycle guard, mirroring the seam's own
 * `collectImagePropsBags` shape.
 *
 * A STRING is handled at exactly two places (FR-R3): the top-level value, and a
 * recovery envelope's `record` (asked of the journal's own envelope guard). Both go
 * through {@link markStoredString}. Every other nested string is NOT parsed.
 */
export function collectReferencedAssetIds(
  root: unknown,
  out: Set<string> = new Set<string>(),
): Set<string> {
  if (typeof root === "string") {
    markStoredString(root, out);
    return out;
  }
  if (isRecoveryEnvelope(root) && typeof root.record === "string") {
    markStoredString(root.record, out);
  }
  const seen = new WeakSet<object>();
  const stack: unknown[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || typeof node !== "object") continue;
    if (seen.has(node as object)) continue;
    seen.add(node as object);

    if (Array.isArray(node)) {
      for (const el of node) stack.push(el);
      continue;
    }
    const rec = node as Record<string, unknown>;
    const ref = rec[ASSET_REF_FIELD];
    if (typeof ref === "string" && ref.length > 0) out.add(ref);
    for (const key in rec) {
      const value = rec[key];
      if (value && typeof value === "object") stack.push(value);
    }
  }
  return out;
}

export interface AssetGcResult {
  /** Always true: the sweep deletes nothing (hotfix-sweep-s2; see module doc). */
  reportOnly: true;
  /** Blobs enumerated in the store (the candidate set). */
  scanned: number;
  /** Distinct referenced ids handed in (the mark set). */
  referenced: number;
  /** Session-claimed ids excluded from {@link wouldDelete}. */
  claimed: number;
  /** Ids neither referenced nor claimed — what a deleting sweep would remove. Nothing was deleted. */
  wouldDelete: string[];
  /** Non-null when the store could not be enumerated (nothing listed). */
  refusedReason: "keys-enumeration-failed" | null;
}

/**
 * The pure SWEEP, REPORT-ONLY: list every stored blob that is neither referenced
 * by the (COMPLETE, caller-guaranteed) `referencedIds` set nor session-claimed,
 * and DELETE NOTHING (hotfix-sweep-s2; see module doc). If the store cannot
 * enumerate its keys, REFUSE — an un-listable store is ambiguous.
 */
export async function assetGarbageCollect(
  store: AssetStore,
  referencedIds: ReadonlySet<string>,
): Promise<AssetGcResult> {
  let keys: string[];
  try {
    keys = await store.keys();
  } catch (error) {
    logger.error("assetGarbageCollect: keys() failed — sweep REFUSED (delete nothing)", {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return {
      reportOnly: true,
      scanned: 0,
      referenced: referencedIds.size,
      claimed: store.claimedIds().size,
      wouldDelete: [],
      refusedReason: "keys-enumeration-failed",
    };
  }
  // Snapshot claims AFTER keys() (claim-first put ordering ⇒ any blob visible in
  // keys() was claimed no later, so this snapshot sees its claim — the race guard).
  const claimed = store.claimedIds();
  const wouldDelete: string[] = [];
  for (const id of keys) {
    if (referencedIds.has(id)) continue; // still referenced by a live document
    if (claimed.has(id)) continue; // stored this session (race guard)
    wouldDelete.push(id); // REPORT-ONLY: listed, never deleted
  }
  return {
    reportOnly: true,
    scanned: keys.length,
    referenced: referencedIds.size,
    claimed: claimed.size,
    wouldDelete,
    refusedReason: null,
  };
}

export interface CorpusReferencedResult {
  referencedIds: Set<string>;
  /** False ⇒ the corpus could not be fully enumerated/read ⇒ the caller must REFUSE. */
  complete: boolean;
}

/** Scan localStorage for `project_*` document keys (sync, fail-safe → []). */
function scanLocalStorageProjectKeys(): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith("project_")) out.push(key);
    }
  } catch {
    // localStorage unavailable — the union + completeness check below handle it.
  }
  return out;
}

/**
 * MARK phase: assemble the COMPLETE set of asset ids referenced by every live
 * document across BOTH tiers. Returns `complete: false` (⇒ refuse the sweep) on
 * ANY enumeration/read ambiguity — the load-bearing D1 guard against deleting a
 * referenced blob.
 *
 * Fail-silent defense: `indexedDBManager.getKeysByPrefix` returns `[]` when the
 * document DB is unavailable (it swallows the open failure), which would read as
 * "no document references anything" → sweep everything. We refuse that by
 * requiring EVERY live project to contribute ≥1 enumerated document key across
 * the two tiers; a live project with zero keys means the enumeration is suspect
 * (or the project is mid-delete) → INCOMPLETE → refuse.
 *
 * @param liveProjectIds ids from the successfully-loaded project registry (the
 *   caller — `bootstrapStorageFoundation` — already refuses when the registry is
 *   unreadable, so a non-null list here means the registry is authoritative). An
 *   affirmatively-EMPTY list is valid: zero live projects ⇒ zero references ⇒ the
 *   sweep lists every blob (mirrors `bootOrphanSweep`; both are report-only).
 */
export async function collectBootReferencedAssetIds(
  liveProjectIds: string[],
): Promise<CorpusReferencedResult> {
  const referencedIds = new Set<string>();

  let idbKeys: string[] = [];
  try {
    idbKeys = await indexedDBManager.getKeysByPrefix("project_");
  } catch {
    // getKeysByPrefix already swallows DB-unavailable → []; a thrown error here is
    // an enumeration failure. Either way the completeness check below refuses if a
    // live project ends up with zero keys.
    idbKeys = [];
  }
  const lsKeys = scanLocalStorageProjectKeys();
  const idbSet = new Set(idbKeys);
  const lsSet = new Set(lsKeys);
  const allKeys = Array.from(new Set([...idbKeys, ...lsKeys]));

  // COMPLETENESS: every live project must contribute at least one enumerated
  // document key. A live project enumerating to zero ⇒ the scan is suspect (the
  // fail-silent [] case, or a mid-delete) ⇒ refuse rather than under-mark.
  for (const projectId of liveProjectIds) {
    const hasAnyKey = allKeys.some((k) => isProjectRecordKey(k, projectId));
    if (!hasAnyKey) {
      logger.warn(
        "collectBootReferencedAssetIds: live project has zero enumerated document keys — corpus INCOMPLETE, sweep will refuse",
        { data: { projectId } },
      );
      return { referencedIds, complete: false };
    }
  }

  // Read each enumerated document and collect its references. Any READ failure ⇒
  // INCOMPLETE (we cannot prove those references absent). A localStorage value is
  // handed over RAW: the collector's string handling parses it, or scans it liberally
  // when it does not parse — so a parse failure never makes the corpus incomplete
  // (snapshots-p2a-s10 D8, FR-P5).
  for (const key of allKeys) {
    let value: unknown = undefined;
    try {
      if (idbSet.has(key)) {
        const result = await indexedDBManager.load<unknown>(key);
        if (result.success && result.data !== undefined) value = result.data;
      }
      if (value === undefined && lsSet.has(key)) {
        const raw = localStorage.getItem(key);
        if (raw !== null) value = raw;
      }
    } catch {
      logger.warn(
        "collectBootReferencedAssetIds: document read failed — corpus INCOMPLETE, sweep will refuse",
        { data: { key } },
      );
      return { referencedIds, complete: false };
    }
    if (value === undefined) {
      // Enumerated but unreadable on both tiers — cannot prove its refs absent.
      logger.warn(
        "collectBootReferencedAssetIds: enumerated document unreadable — corpus INCOMPLETE, sweep will refuse",
        { data: { key } },
      );
      return { referencedIds, complete: false };
    }
    collectReferencedAssetIds(value, referencedIds);
  }

  // RE-ENUMERATE after every read (snapshots-p2a-s11 H1, CTO 66-13). Each read above is
  // its own transaction, so a save can commit between the enumeration and a read — and an
  // emptying save moves the stored drawing into a NEW recovery key the first enumeration
  // never listed, leaving its images unmarked. Any key absent from the first enumeration ⇒
  // the corpus is not a consistent snapshot ⇒ INCOMPLETE ⇒ the sweep refuses. A save after
  // this re-check is harmless: what it replaces was already read and marked.
  let idbKeysAfter: string[] | null = null;
  try {
    const keys = await indexedDBManager.getKeysByPrefix("project_");
    if (Array.isArray(keys)) idbKeysAfter = keys;
  } catch {
    // handled below: an unconfirmed snapshot is never treated as complete
  }
  if (idbKeysAfter === null) {
    logger.warn(
      "collectBootReferencedAssetIds: re-enumeration failed — corpus INCOMPLETE, sweep will refuse",
    );
    return { referencedIds, complete: false };
  }
  const allKeySet = new Set(allKeys);
  const appeared = [...idbKeysAfter, ...scanLocalStorageProjectKeys()].filter(
    (k) => !allKeySet.has(k),
  );
  if (appeared.length > 0) {
    logger.warn(
      "collectBootReferencedAssetIds: keys appeared during the corpus read — corpus INCOMPLETE, sweep will refuse",
      { data: { appeared } },
    );
    return { referencedIds, complete: false };
  }

  return { referencedIds, complete: true };
}

export interface BootAssetGcResult {
  gc: AssetGcResult | null;
  refusedReason: "corpus-incomplete" | null;
}

/**
 * BOOT asset-GC entry point — REPORT-ONLY since hotfix-sweep-s2. Assembles the
 * complete referenced set from the live corpus and runs the report-only sweep,
 * logging ONE error-level report of the blobs it would delete — or REFUSES if the
 * corpus could not be fully read. Deletes nothing either way. Never throws.
 *
 * @param liveProjectIds authoritative live-project ids (registry loaded OK).
 * @param store defaults to the production singleton; injectable for tests.
 */
export async function runBootAssetGc(
  liveProjectIds: string[],
  store: AssetStore = productionAssetStore,
): Promise<BootAssetGcResult> {
  try {
    const { referencedIds, complete } =
      await collectBootReferencedAssetIds(liveProjectIds);
    if (!complete) {
      logger.warn(
        "runBootAssetGc: referenced-set INCOMPLETE — asset sweep REFUSED (never delete on ambiguity)",
      );
      return { gc: null, refusedReason: "corpus-incomplete" };
    }
    const gc = await assetGarbageCollect(store, referencedIds);
    if (gc.wouldDelete.length > 0) {
      logger.error(
        "runBootAssetGc: REPORT-ONLY — unreferenced asset blobs were left in place (nothing deleted)",
        {
          data: {
            wouldDelete: gc.wouldDelete,
            wouldDeleteCount: gc.wouldDelete.length,
            scanned: gc.scanned,
            referenced: gc.referenced,
            claimed: gc.claimed,
          },
        },
      );
    }
    return { gc, refusedReason: null };
  } catch (error) {
    // Never let asset GC fail the boot. On any unexpected error, delete nothing.
    logger.error("runBootAssetGc: unexpected failure — asset sweep skipped", {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return { gc: null, refusedReason: "corpus-incomplete" };
  }
}
